import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  CustomIconButton,
  CloseRounded,
  useMediaQuery,
  MuiLoadingButton,
  DialogActions,
  DialogContentText,
  Grid,
} from "components/common";
import { Card, CardContent } from "@mui/material";
import { theme } from "theme";
import { useState, useEffect } from "react";
import axiosInstance from "utils/axios";
import { showLoadingToast, updateToast } from "utils/toast";
import { useAuthDataState } from "states/authState";

interface TrackOrderModalProps {
  open: boolean;
  onClose: () => void;
}

interface OrderStatus {
  orderId: string;
  status: string;
  createdAt: string | { seconds: number; nanoseconds: number };
  contact_info: {
    first_name: string;
    last_name: string;
    email: string;
  };
  order: {
    purchase_units: Array<{
      amount: {
        value: string;
        currency_code: string;
      };
    }>;
  };
}

export default function TrackOrderModal({ open, onClose }: TrackOrderModalProps) {
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [orders, setOrders] = useState<OrderStatus[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [authState] = useAuthDataState();

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case "PENDING":
        return "#FFA500"; // Orange
      case "PROCESSING":
        return "#2196F3"; // Blue
      case "SHIPPED":
        return "#4CAF50"; // Green
      case "DELIVERED":
        return "#4CAF50"; // Green
      case "CANCELLED":
        return "#F44336";
      case "ISSUE":
        return "#F44336"; // Red
      case "REFUND":
        return "#9C27B0"; // Purple
      default:
        return "#666"; // Gray
    }
  };

  const getStatusText = (status: string) => {
    switch (status?.toUpperCase()) {
      case "PENDING":
        return "Order is pending and will be processed soon";
      case "PROCESSING":
        return "Your order is being processed and prepared for shipping";
      case "SHIPPED":
        return "Your order has been shipped and is on its way";
      case "DELIVERED":
        return "Your order has been delivered successfully";
      case "CANCELLED":
        return "Your order has been cancelled";
      case "ISSUE":
        return "There's an issue delivering your order";
      case "REFUND":
        return "Your order has been refunded";
      default:
        return "Order status unknown";
    }
  };

  const fetchOrders = async () => {
    if (!authState.isLoggedIn) {
      updateToast("", {
        render: "Please login to view your orders",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
      return;
    }

    setIsLoading(true);
    const id = showLoadingToast("Loading your orders...");

    try {
      const response = await axiosInstance.get("/orders/my-orders");
      setOrders(response.data.orders || []);
      updateToast(id, {
        render: "Orders loaded successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error: any) {
      const message = error.response?.data?.message || "Failed to load orders";
      updateToast(id, {
        render: message,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open && authState.isLoggedIn) {
      fetchOrders();
    }
  }, [open, authState.isLoggedIn]);

  const handleClose = () => {
    setOrders([]);
    onClose();
  };

  const formatDate = (dateString: string | any) => {
    let date;
    
    // Handle Firebase Timestamp
    if (dateString && typeof dateString === 'object' && dateString.seconds) {
      date = new Date(dateString.seconds * 1000);
    } else if (dateString) {
      date = new Date(dateString);
    } else {
      return 'Date not available';
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "800px",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogContent>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Box display={"flex"} alignItems="center" gap={3}>
            <CustomIconButton color="primary" onClick={handleClose}>
              <CloseRounded />
            </CustomIconButton>
            <DialogTitle
              sx={{
                color: "#666",
                fontSize: "36px",
              }}
            >
              My Orders
            </DialogTitle>
          </Box>
        </Box>

        {!authState.isLoggedIn ? (
          <Box textAlign="center" py={4}>
            <DialogContentText>
              Please login to view your order history.
            </DialogContentText>
            <MuiLoadingButton
              variant="contained"
              onClick={handleClose}
              sx={{ mt: 2 }}
            >
              Close
            </MuiLoadingButton>
          </Box>
        ) : (
          <>
            <DialogContentText mb={3}>
              Here are all your orders and their current status:
            </DialogContentText>
            
            {isLoading ? (
              <Box textAlign="center" py={4}>
                <Typography>Loading your orders...</Typography>
              </Box>
            ) : orders.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary">
                  You don't have any orders yet.
                </Typography>
                <MuiLoadingButton
                  variant="outlined"
                  onClick={fetchOrders}
                  sx={{ mt: 2 }}
                >
                  Refresh
                </MuiLoadingButton>
              </Box>
            ) : (
              <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
                <Grid container spacing={2}>
                  {orders.map((order, index) => (
                    <Grid xs={12} key={order.orderId || index}>
                      <Card 
                        sx={{ 
                          border: `2px solid ${getStatusColor(order.status)}`,
                          borderRadius: 2
                        }}
                      >
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                            <Box>
                              <Typography variant="h6" color="primary" gutterBottom>
                                Order #{order.orderId}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(order.createdAt)}
                              </Typography>
                            </Box>
                            <Box textAlign="right">
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  color: getStatusColor(order.status),
                                  fontWeight: "bold"
                                }}
                              >
                                {order.status?.toUpperCase()}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {order.order?.purchase_units?.[0]?.amount?.value || '0'} {order.order?.purchase_units?.[0]?.amount?.currency_code || 'USD'}
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" mb={1}>
                            {getStatusText(order.status)}
                          </Typography>
                          
                          <Box sx={{ p: 1, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                            <Typography variant="body2">
                              <strong>Customer:</strong> {order.contact_info?.first_name || ''} {order.contact_info?.last_name || ''}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Email:</strong> {order.contact_info?.email || 'Not available'}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            
            <DialogActions sx={{ mt: 2 }}>
              <MuiLoadingButton
                variant="outlined"
                onClick={fetchOrders}
                loading={isLoading}
                sx={{ width: "100%" }}
              >
                Refresh Orders
              </MuiLoadingButton>
            </DialogActions>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
} 